import React from 'react';
import { Helmet } from 'react-helmet-async';

const Financing = () => {
  // FinancialService schema for SEO
  const financingSchema = {
    "@context": "https://schema.org",
    "@type": "FinancialService",
    "name": "Fort Worth Dental Financing",
    "description": "Flexible dental financing options including CareCredit, payment plans, and insurance acceptance for dental implants, Invisalign, and all dental procedures.",
    "url": "https://fortworthdental.com/form-finances",
    "provider": {
      "@type": "Dentist",
      "name": "Fort Worth Dental",
      "telephone": "(817) 985-3337",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "4620 Citylake Blvd W",
        "addressLocality": "Fort Worth",
        "addressRegion": "TX",
        "postalCode": "76132"
      }
    },
    "serviceType": ["Dental Financing", "CareCredit", "Payment Plans", "Insurance Processing"],
    "areaServed": {
      "@type": "City",
      "name": "Fort Worth",
      "addressRegion": "TX"
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Dental Financing & CareCredit | Payment Options | Fort Worth Dental</title>
        <meta name="description" content="Affordable dental care financing at Fort Worth Dental. CareCredit 0% interest, flexible payment plans, most insurance accepted. Make dental implants, Invisalign affordable. Call (817) 985-3337." />
        <meta name="keywords" content="dental financing fort worth, carecredit dentist, dental payment plans, affordable dental care, dental insurance fort worth tx, no interest dental financing" />
        <link rel="canonical" href="https://fortworthdental.com/form-finances" />
        <script type="application/ld+json">{JSON.stringify(financingSchema)}</script>
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-600 to-teal-800 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Financial Policies, CareCredit & Patient Forms</h1>
          <p className="text-xl max-w-3xl mx-auto">
            We make paying for your dental care simple and affordable
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          
          {/* Patient Form Downloads */}
          <section className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-4xl font-bold text-gray-800 mb-6">Patient Form Downloads</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Fort Worth Dental has posted all of our new-patient paperwork online in order to make it conveniently 
                  accessible. In a matter of minutes, you can easily download the forms and complete them at home. By bringing 
                  in the completed forms to your first appointment, your first visit will be smooth and simple at the front desk.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Please contact our office if you encounter any difficulty while downloading the forms. One of our friendly 
                  and knowledgeable staff members will gladly provide assistance with the forms.
                </p>
              </div>
              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">New Patient Forms</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Please call our office at <a href="tel:8179853337" className="text-teal-600 font-medium">(817) 985-3337</a> to 
                  receive your patient portal login credentials, or complete our downloadable forms below.
                </p>
                <div className="space-y-4">
                  <a 
                    href="/forms/FWD-patientform.pdf" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block bg-teal-600 text-white text-center px-6 py-3 rounded-lg hover:bg-teal-700"
                  >
                    Download Patient Information Form
                  </a>
                  <a 
                    href="/forms/LANAP-Post-Op.pdf" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block bg-gray-700 text-white text-center px-6 py-3 rounded-lg hover:bg-gray-800"
                  >
                    LANAP Post Operative Instructions
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Payment Options */}
          <section className="mb-16 bg-gray-50 p-12 rounded-lg">
            <h2 className="text-4xl font-bold text-gray-800 mb-6 text-center">
              Fort Worth Dental Payment Options
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed text-center mb-8 max-w-4xl mx-auto">
              Ability to pay is of deep concern to many dental patients. We share your concern and are prepared to offer you 
              a number of payment options.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="text-4xl mb-4 text-teal-600">🏥</div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">Insurance</h3>
                <p className="text-gray-600">
                  If you have dental insurance, we file claims as a courtesy for our patients.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="text-4xl mb-4 text-teal-600">💳</div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">Credit Cards</h3>
                <p className="text-gray-600">
                  We accept Visa, MasterCard, American Express, Discover and Care Credit.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="text-4xl mb-4 text-teal-600">📋</div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">Special Offers</h3>
                <p className="text-gray-600">
                  Discounts cannot be combined. Only one special offer may be used per customer.
                </p>
              </div>
            </div>
          </section>

          {/* CareCredit */}
          <section className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-gray-800 mb-6">Need Dental Financing? CareCredit can help!</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  We are pleased to offer CareCredit to our patients. CareCredit is a convenient, no initial payment, 
                  low monthly payment plan for dental treatments of $1,000 to $25,000.
                </p>
                <div className="space-y-4">
                  <a 
                    href="https://www.carecredit.com/apply/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-block bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-teal-700"
                  >
                    Apply for CareCredit
                  </a>
                  <a 
                    href="https://www.carecredit.com/login/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-block bg-gray-700 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-800 ml-4"
                  >
                    Make a Payment
                  </a>
                </div>
              </div>
              <div className="bg-gray-100 p-8 rounded-lg flex items-center justify-center">
                <div className="bg-white px-8 py-6 rounded-lg shadow-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-3xl font-bold text-green-600">Care</span>
                    <span className="text-3xl font-bold text-blue-900">Credit</span>
                    <sup className="text-blue-900 text-sm">®</sup>
                  </div>
                  <div className="text-xs text-gray-500 mt-1 text-center">Healthcare Financing</div>
                </div>
              </div>
            </div>
          </section>

          {/* Lending Club */}
          <section className="mb-16 bg-teal-50 p-12 rounded-lg">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="flex items-center justify-center">
                <div className="bg-white px-8 py-6 rounded-lg shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-xl">LC</span>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-blue-800">LendingClub</div>
                      <div className="text-sm text-gray-600 font-medium">Patient Solutions</div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h2 className="text-4xl font-bold text-gray-800 mb-6">True No-Interest Plan</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Our practice is pleased to present flexible payment plans through Lending Club Patient Solutions.
                </p>
                <ul className="space-y-3 text-gray-700 mb-6">
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2">✓</span>
                    <span>Plans with low fixed rates & low monthly payments</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2">✓</span>
                    <span>Patient-friendly, True No-Interest Plans</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2">✓</span>
                    <span>No upfront payments</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2">✓</span>
                    <span>No prepayment penalty</span>
                  </li>
                </ul>
                <div className="space-y-4">
                  <a 
                    href="https://www.lendingclub.com/patientsolutions/app/check-your-rate?clientid=104739" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-block bg-teal-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-teal-700"
                  >
                    Check Your Rate
                  </a>
                  <a 
                    href="https://www.lendingclub.com/patientsolutions/payment_calculator?inbound_client_id=105056" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-block bg-gray-700 text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 ml-4"
                  >
                    Payment Calculator
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Invisalign Financing */}
          <section className="mb-16">
            <div className="bg-purple-50 p-12 rounded-lg text-center">
              <h2 className="text-4xl font-bold text-gray-800 mb-6">Special Financing - for Invisalign® ONLY</h2>
              <p className="text-gray-700 text-lg mb-8">
                Flexible financing options specifically designed for Invisalign treatment
              </p>
              <a 
                href="https://www.lendingpoint.com/merchants/invisalign/location/ALGN_LH_FORT_6199/ALGN_LH_FORT_6199_PON_URL" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700"
              >
                Apply for Invisalign Financing
              </a>
            </div>
          </section>

          {/* Understanding Insurance */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-6">Understanding Your Insurance Options</h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              At Fort Worth Dental, we understand that navigating dental insurance can be challenging for many patients. 
              That's why we provide detailed information about the types of insurance plans we accept, ensuring that you 
              know what to expect regarding coverage and benefits during your visit.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              We accept most major dental insurance plans and file claims as a courtesy to our patients. This means less 
              hassle for you and more focus on your dental care. If you're unsure about your coverage, our team is here 
              to assist you in verifying your benefits before your appointment.
            </p>
          </section>

          {/* Office Tour Video */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-6 text-center">Take a Virtual Tour of Our Office</h2>
            <div className="aspect-video rounded-lg overflow-hidden shadow-xl max-w-4xl mx-auto">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/phPxGtax2LI"
                title="Fort Worth Dental Office Tour"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </section>

          {/* CTA */}
          <section className="bg-teal-600 text-white p-12 rounded-lg text-center">
            <h2 className="text-3xl font-bold mb-4">Questions About Financing or Forms?</h2>
            <p className="text-xl mb-8">Our team is here to help you understand your options</p>
            <a href="tel:8179853337" className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 inline-block">
              Call (817) 985-3337
            </a>
          </section>

        </div>
      </div>
    </div>
  );
};

export default Financing;
